import type { PageLocationCitation } from '../types';
import { Schema } from 'effect';

export type LocalDocumentStatus = 'processing' | 'ready' | 'failed';

export interface LocalToolAvailability {
  available: boolean;
  path: string | null;
}

export interface LocalDocumentExtractor {
  pdftotext: LocalToolAvailability;
  pdfinfo: LocalToolAvailability;
  pdftoppm: LocalToolAvailability;
  tesseract: LocalToolAvailability;
  ocrUsed: boolean;
}

export interface LocalDocumentPageRecord {
  pageNumber: number;
  textPath: string;
  charCount: number;
  excerpt: string;
  ocrApplied: boolean;
}

export interface LocalDocumentRecord {
  documentId: string;
  filename: string;
  storedPath: string;
  sourcePath: string;
  status: LocalDocumentStatus;
  pageCount: number;
  charCount: number;
  createdAt: string;
  updatedAt: string;
  error: string | null;
  extractor: LocalDocumentExtractor;
  pages: LocalDocumentPageRecord[];
}

/**
 * Self-host citations use the same Anthropic `page_location` shape as the cloud
 * surfaces (M-CITE / #580) — "one citation shape everywhere". `citation_url`
 * points at the stored source PDF at the cited page (`file://…#page=N`), the
 * honest local proof; no `bbox` (the text-only local path has no element boxes).
 */
export type LocalCitation = PageLocationCitation;

export interface LocalTableCandidate {
  page: number;
  preview: string;
  rowCount: number;
  score?: number;
}

export interface LocalDoctorReport {
  ok: boolean;
  dataDir: string;
  tools: {
    pdftotext: LocalToolAvailability;
    pdfinfo: LocalToolAvailability;
    pdftoppm: LocalToolAvailability;
    tesseract: LocalToolAvailability;
  };
}

export const localDocumentStatusSchema = Schema.Literals(['processing', 'ready', 'failed']);

export const localToolAvailabilitySchema = Schema.Struct({
  available: Schema.Boolean,
  path: Schema.NullOr(Schema.String),
});

export const localDocumentExtractorSchema = Schema.Struct({
  pdftotext: localToolAvailabilitySchema,
  pdfinfo: localToolAvailabilitySchema,
  pdftoppm: localToolAvailabilitySchema,
  tesseract: localToolAvailabilitySchema,
  ocrUsed: Schema.Boolean,
});

export const localDocumentPageRecordSchema = Schema.Struct({
  pageNumber: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  textPath: Schema.String,
  charCount: Schema.Natural,
  excerpt: Schema.String,
  ocrApplied: Schema.Boolean,
});

/** Runtime contract for persisted `meta.json` records in the zero-cloud local harness. */
export const localDocumentRecordSchema = Schema.Struct({
  documentId: Schema.String,
  filename: Schema.String,
  storedPath: Schema.String,
  sourcePath: Schema.String,
  status: localDocumentStatusSchema,
  pageCount: Schema.Natural,
  charCount: Schema.Natural,
  createdAt: Schema.String,
  updatedAt: Schema.String,
  error: Schema.NullOr(Schema.String),
  extractor: localDocumentExtractorSchema,
  pages: Schema.Array(localDocumentPageRecordSchema),
});
